<template>
  <div v-tooltip="{
    content: tooltipContent,
    html: true,
  }" class="timeline-end">
    <UIInputDate
      :key="inputKey"
      :model-value="modelValue"
      v-bind="props"
      blur-after-select
      @update:model-value="handleUpdate"
      @blur="handleSubmit"
      @click:enter="handleSubmit"
    >
      <template #leading>
        <div @click="handleUpdateEndIsToday">
          <component
            :is="endIsToday ? LockOpenIcon : LockClosedIcon"
            class="timeline-end__pin"
            :class="pinClasses"
          />
        </div>
      </template>
    </UIInputDate>
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue'

import { InputSize, RepositoryTimeline } from '@types'

import { stringToDateTime } from '@/helpers/dates'

import { UIInputDate } from '@ui/fields'
import { LockClosedIcon, LockOpenIcon } from '@heroicons/vue/24/outline'

type Props = {
  disabled?: boolean
  size?: InputSize
}

type Emits = {
  update: []
}

const props = defineProps<Props>()
const emit = defineEmits<Emits>()

const modelValue = defineModel<RepositoryTimeline['end']>()
const endIsToday = defineModel<RepositoryTimeline['end_is_today']>('end-is-today')

defineOptions({
  name: 'TimelineEnd',
})

const innerData = ref(modelValue.value || null)
const inputKey = ref(0)

const wasChanged = ref(false)

const pinClasses = computed(() => ({
  'timeline-end__pin--active': !endIsToday.value,
}))

const tooltipContent = computed(() => ([
  '<p class="timeline-end__tooltip-title">Analytics end date</p>',
  '<p class="timeline-end__tooltip-line"><svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" aria-hidden="true" data-slot="icon" class="timeline-end__tooltip-lock timeline-end__tooltip-lock--active"><path stroke-linecap="round" stroke-linejoin="round" d="M16.5 10.5V6.75a4.5 4.5 0 1 0-9 0v3.75m-.75 11.25h10.5a2.25 2.25 0 0 0 2.25-2.25v-6.75a2.25 2.25 0 0 0-2.25-2.25H6.75a2.25 2.25 0 0 0-2.25 2.25v6.75a2.25 2.25 0 0 0 2.25 2.25Z"></path></svg><span>the date is frozen and can only be <br />changed manually by the user</span></p>',
  '<p class="timeline-end__tooltip-line"><svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" aria-hidden="true" data-slot="icon" class="timeline-end__tooltip-lock"><path stroke-linecap="round" stroke-linejoin="round" d="M13.5 10.5V6.75a4.5 4.5 0 1 1 9 0v3.75M3.75 21.75h10.5a2.25 2.25 0 0 0 2.25-2.25v-6.75a2.25 2.25 0 0 0-2.25-2.25H3.75a2.25 2.25 0 0 0-2.25 2.25v6.75a2.25 2.25 0 0 0 2.25 2.25Z"></path></svg><span>the date is automatically set to the <br />current date on every refresh</span></p>'
].join('')))

const handleUpdate = (value: string | null) => {
  wasChanged.value = true
  innerData.value = value
  const date = stringToDateTime(value)
  if (!date?.isValid) return
  modelValue.value = value || undefined
}

const handleUpdateEndIsToday = () => {
  endIsToday.value = !endIsToday.value
}

const isValidDate = (value: string | null) => {
  const date = stringToDateTime(value)
  return date?.isValid
}

const handleSubmit = () => {
  if (!wasChanged.value) return
  if (!isValidDate(innerData.value)) {
    inputKey.value++
    return
  }
  wasChanged.value = false
  emit('update')
}
</script>

<style>
.timeline-end {
  @apply flex-auto;

  &__pin {
    @apply text-gray-200 hover:text-gray-300;
    @apply dark:text-gray-400 dark:hover:text-gray-200;
    @apply cursor-pointer;

    &--active {
      @apply text-red-500 hover:text-red-600;
      @apply dark:text-red-400 dark:hover:text-red-300;
    }
  }

  .ui-input-text-icon--leading {
    @apply w-6 lg:w-8;

    svg {
      @apply size-4;
    }
  }

  .ui-input-text__input--leading {
    @apply pl-6 lg:pl-8;
  }

  .ui-input-text__input--trailing {
    @apply pr-8 lg:pr-9;
  }

  &__tooltip-title {
    @apply font-medium;
  }

  &__tooltip-line {
    @apply flex items-start gap-2;
    @apply mt-2;
    @apply leading-none;
    @apply text-gray-300 dark:text-gray-500;
  }

  &__tooltip-lock {
    @apply size-3;

    &--active {
      @apply text-red-300 dark:text-red-400;
    }
  }
}
</style>
